import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { loadPbrMaps, disposePbrMaps, ensureUv2, getPbrLayout, PBR_SETS, PAINT_SET_KEY } from './pbrTextures.jsx';

// «المان» درِ سه‌بعدی — نُه استایلِ قابل‌انتخاب، هرکدام با نحوه‌ی بازشدنِ متناسبِ خودش:
//   • چرخشیِ روی لولا: یک‌تیکه، دوتیکه، مدرن و شیشه‌ای، قدیمیِ رنگ‌وجنس، فرانسوی (شیشه‌ی قابی)
//   • تاشو (اکاردئونی): یک لولای ثابت به چارچوب + یک مفصلِ وسط که هم‌زمان می‌چرخد و لنگه را تا می‌کند
//   • لغزنده: کشویی تک‌لنگه، کشویی دولنگه (دو لنگه از وسط به دو طرف می‌لغزند)، ریلیِ چوبی (بارن)
// عرض/ارتفاعِ همه‌شان قابل‌تنظیم است (که مستقیماً روی اندازه‌ی بازشوی برش‌خورده از دیوار هم اثر
// می‌گذارد — نگاه کنید به Create3D.jsx/computeWallLocalSegments)، و در حالتِ اول‌شخص با لمسِ نشانه‌ی
// دست (که در Create3D.jsx رندر می‌شود) به‌آرامی باز/بسته می‌شوند — هرکدام با انیمیشنِ مخصوصِ نوعِ خودش.

const DEFAULT_DOOR_COLOR = '#8a5a3b';
const DEFAULT_DOOR_ACCENT = '#3a3a3a';
const OPEN_ANGLE = Math.PI * 0.5; // دقیقاً ۹۰ درجه — مثلِ درِ واقعی که به چارچوب/دیوار عمود می‌ایستد
const SWING_SPEED = 6; // چه‌قدر سریع لولا/لنگه‌ی کشویی به هدف (باز یا بسته) می‌رسد

// ابعادِ واقعیِ چارچوب/لنگه (سانتی‌متر) — عددهای ثابت، نه درصدی از عرض/ارتفاع؛ درصدی بودن باعث می‌شد در
// درِ پهن‌تر قاب‌ها و فاصله‌ها بی‌دلیل کلفت‌تر شوند و حسِ «کارتونی» بدهند
const JAMB_CM = 3; // ضخامتِ تخته‌ی چارچوب (داخلِ بازشو، دو طرف + بالا)
const CASING_WIDTH_CM = 7; // عرضِ «زهوار/فریمِ دورِ در» روی دیوار
const CASING_DEPTH_CM = 2; // برجستگیِ زهوار از سطحِ دیوار (از قرنیز کلفت‌تر، تا قرنیز پشتش تمام شود)
const CASING_REVEAL_CM = 0.5; // فاصله‌ی لبه‌ی زهوار از لبه‌ی داخلیِ چارچوب — لبه‌ی چارچوب دیده شود
const LEAF_GAP_CM = 0.3; // درزِ لنگه با چارچوب (دو طرف و بالا)
const FLOOR_GAP_CM = 1; // فاصله‌ی زیرِ لنگه تا کف
const LEAF_THICKNESS_CM = 4; // ضخامتِ واقعیِ لنگه‌ی در (قبلاً ۶۰٪ ضخامتِ دیوار ≈ ۱۲ سانت بود)
const STOP_WIDTH_CM = 1.2; // زبانه‌ی «دور‌گیر» روی چارچوب که لنگه به آن می‌خورد
const STOP_DEPTH_CM = 1.2;
const WALL_FACE_OFFSET_CM = 0.4; // همان FACE_OFFSET_CM در Wall3D.jsx (سطحِ رویه‌ی دیوار)
const SLAB_CLEAR_CM = 0.3; // فاصله‌ی زهوار از جلوی تخته‌ی سنگِ بیرون‌زده (تا هیچ‌وقت زیرِ آن نرود)

export const DEFAULT_DOOR_STYLE = {
  type: 'single',
  color: DEFAULT_DOOR_COLOR,
  accent: DEFAULT_DOOR_ACCENT,
  widthCm: null,
  heightCm: null,
  material: 'paint',
};

// door.style ممکن است ناقص باشد (مثلاً کاربر فقط رنگ را عوض کرده) — همیشه یک شیءِ کاملِ با پیش‌فرض
// برمی‌گرداند، دقیقاً مثلِ الگوی resolveSideStyle در Wall3D.jsx
export function resolveDoorStyle(door) {
  const s = door?.style;
  return {
    type: s?.type || DEFAULT_DOOR_STYLE.type,
    color: s?.color || DEFAULT_DOOR_STYLE.color,
    accent: s?.accent || DEFAULT_DOOR_STYLE.accent,
    widthCm: s?.widthCm || null,
    heightCm: s?.heightCm || null,
    material: s?.material || DEFAULT_DOOR_STYLE.material,
  };
}

// نوعِ جنسِ سطحِ در ('paint' یعنی همان رنگِ ساده‌ی قبلی) → کلیدِ متناظر در PBR_SETS — دقیقاً همان
// PBR_FRAME_MATERIAL_TO_SET در Window3D.jsx (حتی از همان ستِ metalBrushed استفاده می‌کند، چون همان
// یک فایلِ فلزِ آپلودشده است) — و حالا چوبِ واقعی (woodPine) هم کنارش
export const PBR_DOOR_MATERIAL_TO_SET = {
  metalBrushed: 'metalBrushed',
  woodPine: 'woodPine',
  woodDark: 'woodDark',
};

// یک سطحِ تخت (بدنه‌ی اصلیِ لنگه، یا یکی از نبشی‌های قاب) — چون هرکدام ابعادِ واقعیِ متفاوتی دارند، هرکدام
// نقشه‌ی PBR خودش را با تکرارِ مخصوصِ خودش می‌سازد (دقیقاً همان FrameBar در Window3D.jsx). وقتی
// doorMaterial جنسِ ساده (رنگ) باشد، دقیقاً همان روشنی/زبری/envMapIntensityِ قبلیِ همان نوع حفظ می‌شود؛
// وقتی جنسِ واقعیِ عکس باشد، رنگِ انتخابیِ کاربر همچنان روی خودِ عکس «ضرب» می‌شود
// layoutCm (اختیاری): ابعادِ «رویه‌ی اصلیِ دیده‌شده» برای مقیاسِ بافت، وقتی آن رویه همان x×y جعبه نیست —
// مثلاً تخته‌ی چارچوب که پهنای دیده‌شده‌اش عمقِ دیوار است نه ۳ سانت؛ بدونِ این، رگه‌ی چوب کشیده می‌شد
function DoorSurface({ position, sizeCm, layoutCm, colorValue, doorMaterial, roughness = 0.6, envMapIntensity = 0, metalnessBase = 0 }) {
  const isPbr = Boolean(PBR_DOOR_MATERIAL_TO_SET[doorMaterial]);
  // «رنگ» هم حالا رنگِ واقعی است: بافتِ سطحِ رنگ‌شده (فقط برجستگی/normal) روی همان رنگِ انتخابی، با
  // همان زبری/براقیِ مخصوصِ هر نوعِ در که قبلاً داشت — پس ظاهرِ کلیِ درها عوض نمی‌شود، فقط واقعی‌تر می‌شود
  const isPaint = !isPbr;
  const pbrSet = PBR_SETS[isPbr ? PBR_DOOR_MATERIAL_TO_SET[doorMaterial] : PAINT_SET_KEY];
  const layout = layoutCm || sizeCm;
  const { repeatX, repeatY, rotate } = getPbrLayout(pbrSet, layout[0], layout[1]);
  // فقط ست‌هایی که واقعاً metalnessMap دارند (فلز) فلزی رندر می‌شوند؛ چوب و بقیه metalness=0
  const pbrMetalness = pbrSet?.metalness ? 1 : 0;

  const pbrMaps = useMemo(
    () => loadPbrMaps(pbrSet, repeatX, repeatY, { rotate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPbr, doorMaterial, repeatX, repeatY, rotate]
  );

  useEffect(() => () => disposePbrMaps(pbrMaps), [pbrMaps]);

  return (
    <mesh position={position}>
      <boxGeometry ref={ensureUv2} args={sizeCm} />
      <meshStandardMaterial
        key={isPbr ? `pbr-${doorMaterial}` : 'paint'}
        color={colorValue}
        map={pbrMaps?.map || null}
        normalMap={pbrMaps?.normalMap || null}
        roughnessMap={isPaint ? null : pbrMaps?.roughnessMap || null}
        aoMap={pbrMaps?.aoMap || null}
        metalnessMap={isPbr ? pbrMaps?.metalnessMap || null : null}
        roughness={isPbr ? 1 : roughness}
        metalness={isPbr ? pbrMetalness : metalnessBase}
        envMapIntensity={isPbr ? 1 : envMapIntensity}
      />
    </mesh>
  );
}

// دستگیره در هر دو رویِ لنگه — نسخه‌ی دوم آینه‌ی اولی روی محورِ Z است (three.js با دترمینانِ منفی خودش
// جهتِ رویه‌ها را درست می‌کند)؛ قبلاً دستگیره فقط یک طرفِ در بود
function BothSides({ children }) {
  return (
    <>
      {children}
      <group scale={[1, 1, -1]}>{children}</group>
    </>
  );
}

// محتوای بصریِ یک لنگه — بر اساسِ نوع، یا یک صفحه‌ی تخت (یک‌تیکه/دوتیکه)، یا قاب+شیشه (مدرن)، یا
// صفحه‌ی رنگی + دو پنلِ برجسته + دستگیره (قدیمی). contentX یعنی «لنگه، نسبت به نقطه‌ی لولا، کجا قرار
// دارد» — چون خودِ گروهِ لنگه دور نقطه‌ی لولا می‌چرخد، نه دور مرکز خودش
function DoorLeafContent({ style, leafWidth, height, leafThickness, contentX, contentZ = 0, hingeDir }) {
  const { type, color, accent, material } = style;
  const handleX = hingeDir * (leafWidth / 2 - 6); // نزدیکِ لبه‌ی دورتر از لولا

  if (type === 'glassModern' || type === 'sliding') {
    // قابِ توخالی (چهار نبشیِ نازک دورتادور) به‌جای یک جعبه‌ی توپر — چون جعبه‌ی توپرِ قبلی دقیقاً هم‌اندازه‌ی
    // لنگه بود و شیشه‌ی داخلش را کاملاً می‌پوشاند؛ حالا وسطِ لنگه واقعاً خالی و شیشه‌ای است
    const frameW = Math.max(3, leafWidth * 0.06);
    const paneW = Math.max(1, leafWidth - frameW * 2);
    const paneH = Math.max(1, height - frameW * 2);
    return (
      <group position={[contentX, height / 2, contentZ]}>
        <DoorSurface
          position={[0, (height - frameW) / 2, 0]}
          sizeCm={[leafWidth, frameW, leafThickness]}
          colorValue={accent}
          doorMaterial={material}
          roughness={0.35}
          envMapIntensity={0.6}
          metalnessBase={0.2}
        />
        <DoorSurface
          position={[0, -(height - frameW) / 2, 0]}
          sizeCm={[leafWidth, frameW, leafThickness]}
          colorValue={accent}
          doorMaterial={material}
          roughness={0.35}
          envMapIntensity={0.6}
          metalnessBase={0.2}
        />
        <DoorSurface
          position={[(leafWidth - frameW) / 2, 0, 0]}
          sizeCm={[frameW, height - frameW * 2, leafThickness]}
          colorValue={accent}
          doorMaterial={material}
          roughness={0.35}
          envMapIntensity={0.6}
          metalnessBase={0.2}
        />
        <DoorSurface
          position={[-(leafWidth - frameW) / 2, 0, 0]}
          sizeCm={[frameW, height - frameW * 2, leafThickness]}
          colorValue={accent}
          doorMaterial={material}
          roughness={0.35}
          envMapIntensity={0.6}
          metalnessBase={0.2}
        />
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[paneW, paneH, leafThickness * 0.5]} />
          <meshPhysicalMaterial
            color="#dce8ec"
            transparent
            opacity={0.35}
            roughness={0.06}
            metalness={0}
            envMapIntensity={0.9}
          />
        </mesh>
        <BothSides>
          <mesh position={[handleX, -height * 0.06, leafThickness / 2 + 2]}>
            <boxGeometry args={[2.2, 16, 2.2]} />
            <meshStandardMaterial color="#c9c9c9" roughness={0.3} metalness={0.7} />
          </mesh>
        </BothSides>
      </group>
    );
  }

  if (type === 'french') {
    // شبیهِ «مدرن و شیشه‌ای» ولی به‌جای یک شیشه‌ی بزرگ، چند قابِ کوچک با نبشیِ چوبی‌رنگ بینشان — حسِ
    // درهای فرانسویِ کلاسیک. قابِ بیرونی از رنگ اصلی (color) است، نه رنگِ فلزیِ accent
    const frameW = Math.max(3, leafWidth * 0.08);
    const paneW = Math.max(1, leafWidth - frameW * 2);
    const paneH = Math.max(1, height - frameW * 2);
    const cols = 2;
    const rows = 4;
    const cellW = paneW / cols;
    const cellH = paneH / rows;
    const mullion = 1.8;
    return (
      <group position={[contentX, height / 2, contentZ]}>
        <DoorSurface
          position={[0, (height - frameW) / 2, 0]}
          sizeCm={[leafWidth, frameW, leafThickness]}
          colorValue={color}
          doorMaterial={material}
          roughness={0.55}
          envMapIntensity={0.15}
        />
        <DoorSurface
          position={[0, -(height - frameW) / 2, 0]}
          sizeCm={[leafWidth, frameW, leafThickness]}
          colorValue={color}
          doorMaterial={material}
          roughness={0.55}
          envMapIntensity={0.15}
        />
        <DoorSurface
          position={[(leafWidth - frameW) / 2, 0, 0]}
          sizeCm={[frameW, height - frameW * 2, leafThickness]}
          colorValue={color}
          doorMaterial={material}
          roughness={0.55}
          envMapIntensity={0.15}
        />
        <DoorSurface
          position={[-(leafWidth - frameW) / 2, 0, 0]}
          sizeCm={[frameW, height - frameW * 2, leafThickness]}
          colorValue={color}
          doorMaterial={material}
          roughness={0.55}
          envMapIntensity={0.15}
        />
        {Array.from({ length: rows }).map((_, r) =>
          Array.from({ length: cols }).map((_, c) => {
            const px = -paneW / 2 + cellW * (c + 0.5);
            const py = -paneH / 2 + cellH * (r + 0.5);
            return (
              <mesh key={`${r}-${c}`} position={[px, py, 0]}>
                <boxGeometry args={[Math.max(1, cellW - mullion), Math.max(1, cellH - mullion), leafThickness * 0.4]} />
                <meshPhysicalMaterial
                  color="#dce8ec"
                  transparent
                  opacity={0.3}
                  roughness={0.08}
                  metalness={0}
                  envMapIntensity={0.85}
                />
              </mesh>
            );
          })
        )}
        <BothSides>
          <mesh position={[handleX, -height * 0.06, leafThickness / 2 + 2]}>
            <sphereGeometry args={[2.4, 10, 10]} />
            <meshStandardMaterial color={accent} roughness={0.3} metalness={0.6} />
          </mesh>
        </BothSides>
      </group>
    );
  }

  if (type === 'barn') {
    // درِ کشویی ریلیِ چوبی («بارن») — تخته‌های عمودی به‌جای یک صفحه‌ی صاف، به‌اضافه‌ی یک ریلِ فلزی
    // بالای در که کمی از دو طرفِ لنگه بیرون می‌زند (مشخصه‌ی بصریِ این نوع در). حرکتش هم مثلِ «کشویی»
    // است، فقط این‌جا ظاهرش فرق می‌کند
    const plankCount = 5;
    const plankGap = 1;
    const plankW = leafWidth / plankCount;
    return (
      <group position={[contentX, height / 2, contentZ]}>
        <mesh position={[0, height / 2 + 9, 0]}>
          <boxGeometry args={[leafWidth * 1.3, 4, leafThickness * 1.4]} />
          <meshStandardMaterial color="#2b2b2b" roughness={0.4} metalness={0.6} />
        </mesh>
        <mesh position={[-leafWidth * 0.45, height / 2 + 5, 0]}>
          <boxGeometry args={[6, 6, leafThickness * 1.6]} />
          <meshStandardMaterial color="#3a3a3a" roughness={0.35} metalness={0.65} />
        </mesh>
        <mesh position={[leafWidth * 0.45, height / 2 + 5, 0]}>
          <boxGeometry args={[6, 6, leafThickness * 1.6]} />
          <meshStandardMaterial color="#3a3a3a" roughness={0.35} metalness={0.65} />
        </mesh>
        <DoorSurface
          position={[0, 0, 0]}
          sizeCm={[leafWidth, height, leafThickness]}
          colorValue={color}
          doorMaterial={material}
          roughness={0.75}
          envMapIntensity={0.1}
        />
        {Array.from({ length: plankCount - 1 }).map((_, i) => {
          const px = -leafWidth / 2 + plankW * (i + 1);
          return (
            <mesh key={i} position={[px, 0, leafThickness / 2 + 0.3]}>
              <boxGeometry args={[plankGap, height * 0.94, 0.6]} />
              <meshStandardMaterial color={accent} roughness={0.8} />
            </mesh>
          );
        })}
        <BothSides>
          <mesh position={[handleX, -height * 0.06, leafThickness / 2 + 2]}>
            <boxGeometry args={[3, 14, 2]} />
            <meshStandardMaterial color="#5c5348" roughness={0.5} metalness={0.3} />
          </mesh>
        </BothSides>
      </group>
    );
  }

  if (type === 'classic') {
    const panelInsetX = leafWidth * 0.14;
    const panelW = Math.max(1, leafWidth - panelInsetX * 2);
    const panelH = height * 0.34;
    return (
      <group position={[contentX, height / 2, contentZ]}>
        <DoorSurface
          position={[0, 0, 0]}
          sizeCm={[leafWidth, height, leafThickness]}
          colorValue={color}
          doorMaterial={material}
          roughness={0.55}
          envMapIntensity={0.15}
        />
        <BothSides>
          {[height * 0.22, -height * 0.22].map((offsetY) => (
            <mesh key={offsetY} position={[0, offsetY, leafThickness / 2 + 0.6]}>
              <boxGeometry args={[panelW, panelH, 1.2]} />
              <meshStandardMaterial color={accent} roughness={0.65} envMapIntensity={0.15} />
            </mesh>
          ))}
        </BothSides>
        <BothSides>
          <mesh position={[handleX, -height * 0.06, leafThickness / 2 + 2]}>
            <sphereGeometry args={[2.6, 10, 10]} />
            <meshStandardMaterial color="#d8c48a" roughness={0.3} metalness={0.6} />
          </mesh>
        </BothSides>
      </group>
    );
  }

  // 'single' و 'double' — صفحه‌ی تخت و ساده به‌اضافه‌ی دستگیره
  return (
    <group position={[contentX, height / 2, contentZ]}>
      <DoorSurface
        position={[0, 0, 0]}
        sizeCm={[leafWidth, height, leafThickness]}
        colorValue={color}
        doorMaterial={material}
        roughness={0.6}
        envMapIntensity={0}
      />
      <BothSides>
        <mesh position={[handleX, -height * 0.06, leafThickness / 2 + 2]}>
          <sphereGeometry args={[2.6, 10, 10]} />
          <meshStandardMaterial color={accent} roughness={0.3} metalness={0.5} />
        </mesh>
      </BothSides>
    </group>
  );
}

// یک لنگه‌ی کاملِ چرخان — گروهِ بیرونی دقیقاً روی نقطه‌ی لولا قرار می‌گیرد (hingeX) و خودش می‌چرخد؛
// محتوای لنگه با contentX از این نقطه فاصله می‌گیرد تا لبه‌ی دیگرش (نه مرکزش) روی لولا بنشیند.
// مثلِ درِ واقعی، محورِ لولا روی «لبه‌ی رویی» لنگه است (هم‌سطحِ لبه‌ی چارچوب در سمتی که در به آن باز
// می‌شود) نه وسطِ ضخامتِ دیوار — پس لنگه موقعِ باز شدن در چارچوب/زهوار فرو نمی‌رود. pivotZ جای همین
// محور است و contentZ جای وسطِ ضخامتِ لنگه نسبت به آن
function DoorLeaf({ hingeX, hingeDir, swingSign, leafWidth, height, style, targetAngle, pivotZ, contentZ }) {
  const groupRef = useRef(null);
  const angleRef = useRef(0);

  useFrame((_, delta) => {
    const diff = targetAngle - angleRef.current;
    angleRef.current = Math.abs(diff) < 0.001 ? targetAngle : angleRef.current + diff * Math.min(1, delta * SWING_SPEED);
    if (groupRef.current) groupRef.current.rotation.y = angleRef.current * swingSign;
  });

  const contentX = hingeDir * (leafWidth / 2);

  return (
    <group ref={groupRef} position={[hingeX, 0, pivotZ]}>
      <DoorLeafContent
        style={style}
        leafWidth={leafWidth}
        height={height}
        leafThickness={LEAF_THICKNESS_CM}
        contentX={contentX}
        contentZ={contentZ}
        hingeDir={hingeDir}
      />
    </group>
  );
}

// یک لنگه‌ی لغزنده («کشویی» یا «بارن») — بسته: وسطِ چارچوب؛ باز: هم روی محورِ X کنار می‌رود و هم روی Z
// جلوی رویه‌ی دیوار (و جلوی زهوار) می‌آید، دقیقاً مثلِ درِ کشوییِ روکار. slideDistance دقیقاً همان
// اندازه‌ای است که لبه‌ی عقبیِ لنگه به لبه‌ی چارچوب برسد — یعنی بازشو کامل و تمیز باز می‌شود.
// handleSide مشخص می‌کند دستگیره روی کدام لبه‌ی لنگه باشد؛ جهتِ لغزیدن همیشه برعکسِ آن است
function DoorSlideLeaf({ leafWidth, height, thickness, style, isOpen, restX = 0, mountSign = 1, handleSide = 1, slideDistance }) {
  const groupRef = useRef(null);
  const offsetRef = useRef(restX);
  const zOffsetRef = useRef(0);
  const slideSign = -handleSide;
  const targetOffset = restX + (isOpen ? slideDistance * slideSign : 0);
  const leafThickness = LEAF_THICKNESS_CM;
  // جلوی زهوار (نه داخلِ آن): رویه‌ی دیوار + برجستگیِ زهوار + ۰٫۸ سانت فاصله + نصفِ ضخامتِ لنگه
  const targetZOffset = isOpen
    ? mountSign * (thickness / 2 + WALL_FACE_OFFSET_CM + CASING_DEPTH_CM + 0.8 + leafThickness / 2)
    : 0;

  useFrame((_, delta) => {
    const diffX = targetOffset - offsetRef.current;
    offsetRef.current =
      Math.abs(diffX) < 0.001 ? targetOffset : offsetRef.current + diffX * Math.min(1, delta * SWING_SPEED);
    const diffZ = targetZOffset - zOffsetRef.current;
    zOffsetRef.current =
      Math.abs(diffZ) < 0.001 ? targetZOffset : zOffsetRef.current + diffZ * Math.min(1, delta * SWING_SPEED);
    if (groupRef.current) {
      groupRef.current.position.x = offsetRef.current;
      groupRef.current.position.z = zOffsetRef.current;
    }
  });

  return (
    <group ref={groupRef} position={[restX, 0, 0]}>
      <DoorLeafContent
        style={style}
        leafWidth={leafWidth}
        height={height}
        leafThickness={leafThickness}
        contentX={0}
        hingeDir={handleSide}
      />
    </group>
  );
}

// یک لنگه‌ی تاشو (اکاردئونی، «بی‌فولد») — دو نیم‌پنل با یک مفصلِ وسط: نیم‌پنلِ اول از لولای ثابت به
// چارچوب می‌چرخد و نیم‌پنلِ دوم هم‌زمان در جهتِ مخالف تا می‌شود. لولای اول روی لبه‌ی رویی (مثلِ DoorLeaf) و
// مفصلِ وسط روی لبه‌ی مقابل است — همان‌جایی که در درِ تاشوی واقعی هست — تا دو نیم‌پنل موقعِ تا شدن در هم
// فرو نروند
function DoorFoldLeaf({ width, height, style, swingSign, targetAngle, pivotZ, contentZ }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const angleRef = useRef(0);

  useFrame((_, delta) => {
    const diff = targetAngle - angleRef.current;
    angleRef.current = Math.abs(diff) < 0.001 ? targetAngle : angleRef.current + diff * Math.min(1, delta * SWING_SPEED);
    const t = OPEN_ANGLE > 0 ? angleRef.current / OPEN_ANGLE : 0;
    const outerAngle = angleRef.current;
    const innerAngle = -t * OPEN_ANGLE * 1.7;
    if (outerRef.current) outerRef.current.rotation.y = outerAngle * swingSign;
    if (innerRef.current) innerRef.current.rotation.y = innerAngle * swingSign;
  });

  const panelWidth = (width - LEAF_GAP_CM) / 2;
  const hingeX = -width / 2;

  return (
    <group ref={outerRef} position={[hingeX, 0, pivotZ]}>
      <DoorLeafContent
        style={style}
        leafWidth={panelWidth}
        height={height}
        leafThickness={LEAF_THICKNESS_CM}
        contentX={panelWidth / 2}
        contentZ={contentZ}
        hingeDir={1}
      />
      <group ref={innerRef} position={[panelWidth + LEAF_GAP_CM, 0, contentZ * 2]}>
        <DoorLeafContent
          style={style}
          leafWidth={panelWidth}
          height={height}
          leafThickness={LEAF_THICKNESS_CM}
          contentX={panelWidth / 2}
          contentZ={-contentZ}
          hingeDir={1}
        />
      </group>
    </group>
  );
}

// چارچوبِ ثابتِ در — برخلافِ لنگه‌ها هیچ‌وقت حرکت نمی‌کند:
//   • چارچوب (لایینینگ): دو تخته‌ی کناری + یک تخته‌ی بالا، کلِ ضخامتِ دیوار را می‌پوشانند (دیگر لبه‌ی
//     خامِ دیوار داخلِ بازشو دیده نمی‌شود)
//   • زهوار (فریمِ دورِ در): روی هر دو رویِ دیوار، ۷ سانت عرض و ۲ سانت برجسته، با اتصالِ لب‌به‌لب
//   • زبانه‌ی دورگیر (فقط درهای لولایی/تاشو): لنگه دقیقاً پشتِ آن بسته می‌شود
// همه با همان جنس/رنگِ خودِ در
function DoorFrame({ width, height, thickness, style, frameColor, stopZSign, baseY = 0, casingDrop, protrude }) {
  const W = width;
  const H = height;
  const jambDepth = thickness + WALL_FACE_OFFSET_CM * 2;
  // اگر هر دو اتاقِ دو طرف پایین‌تر از سطحِ مبنا باشند، چارچوب و زهوار هم تا همان کف پایین می‌آیند
  const fb = Math.min(0, baseY);
  // زهوارِ هر رو تا کفِ همان سمت پایین می‌آید (سمتِ پایین‌ترِ پله) — sz=+۱ رویِ a، sz=-۱ رویِ b
  const casingBottom = (sz) => fb - Math.max(0, (sz > 0 ? casingDrop?.a : casingDrop?.b) || 0);
  const faceAbs = thickness / 2 + WALL_FACE_OFFSET_CM;
  const casingInner = W / 2 - JAMB_CM + CASING_REVEAL_CM; // فاصله‌ی لبه‌ی داخلیِ زهوار از مرکز
  const casingLegH = H - JAMB_CM + CASING_REVEAL_CM;
  const casingHeadW = (casingInner + CASING_WIDTH_CM) * 2;
  // اگر رویه‌ی دیوار تخته‌ی سنگ/آجر/… بیرون‌زده داشته باشد، زهوارِ همان سمت باید جلوتر از آن بنشیند
  const slabOut = (p) => (p > 0 ? p + SLAB_CLEAR_CM : 0);
  const casingZFor = (sz) => faceAbs + slabOut(sz > 0 ? protrude?.a : protrude?.b) + CASING_DEPTH_CM / 2;
  const innerW = Math.max(1, W - JAMB_CM * 2);
  const surf = { colorValue: frameColor, doorMaterial: style.material, roughness: 0.55, envMapIntensity: 0.15 };
  // زبانه‌ی دورگیر درست پشتِ لنگه‌ی بسته (لنگه هم‌سطحِ لبه‌ی چارچوب در سمتِ بازشدن است)
  const stopZ = stopZSign ? stopZSign * (faceAbs - LEAF_THICKNESS_CM - STOP_DEPTH_CM / 2) : 0;

  return (
    <group>
      {[-1, 1].map((sx) => (
        <DoorSurface
          key={`jamb${sx}`}
          position={[sx * (W / 2 - JAMB_CM / 2), (fb + H) / 2, 0]}
          sizeCm={[JAMB_CM, H - fb, jambDepth]}
          layoutCm={[jambDepth, H - fb]}
          {...surf}
        />
      ))}
      <DoorSurface
        position={[0, H - JAMB_CM / 2, 0]}
        sizeCm={[innerW, JAMB_CM, jambDepth]}
        layoutCm={[innerW, jambDepth]}
        {...surf}
      />
      {[-1, 1].map((sz) => (
        <group key={`casing${sz}`}>
          {[-1, 1].map((sx) => (
            <DoorSurface
              key={sx}
              position={[sx * (casingInner + CASING_WIDTH_CM / 2), (casingBottom(sz) + casingLegH) / 2, sz * casingZFor(sz)]}
              sizeCm={[CASING_WIDTH_CM, casingLegH - casingBottom(sz), CASING_DEPTH_CM]}
              {...surf}
            />
          ))}
          <DoorSurface
            position={[0, casingLegH + CASING_WIDTH_CM / 2, sz * casingZFor(sz)]}
            sizeCm={[casingHeadW, CASING_WIDTH_CM, CASING_DEPTH_CM]}
            {...surf}
          />
        </group>
      ))}
      {stopZSign !== 0 && (
        <group>
          {[-1, 1].map((sx) => (
            <DoorSurface
              key={`stop${sx}`}
              position={[sx * (innerW / 2 - STOP_WIDTH_CM / 2), (baseY + H - JAMB_CM) / 2, stopZ]}
              sizeCm={[STOP_WIDTH_CM, H - JAMB_CM - baseY, STOP_DEPTH_CM]}
              {...surf}
            />
          ))}
          <DoorSurface
            position={[0, H - JAMB_CM - STOP_WIDTH_CM / 2, stopZ]}
            sizeCm={[innerW - STOP_WIDTH_CM * 2, STOP_WIDTH_CM, STOP_DEPTH_CM]}
            {...surf}
          />
        </group>
      )}
    </group>
  );
}

// لبه‌ی زهوار چه‌قدر روی دیوارِ کنارِ بازشو می‌نشیند (برای اطلاعِ بقیه‌ی کد)
export const DOOR_CASING_OVERLAP_CM = CASING_WIDTH_CM - JAMB_CM + CASING_REVEAL_CM;

// خودِ در — چارچوبِ ثابت + یک یا دو لنگه (بسته به style.type)، به‌اضافه‌ی نوارِ آبیِ انتخاب (وقتی در
// حالت اوربیت لمس شده باشد). width/height اندازه‌ی «بازشوی برش‌خورده در دیوار» است؛ لنگه‌ها دقیقاً داخلِ
// چارچوب با درزِ ثابتِ ۳ میلی‌متر ساخته می‌شوند (نه درصدی از عرض)
// baseY: اگر یکی از دو اتاقِ دو طرفِ در کفِ بالاتری داشته باشد (اختلافِ سطح)، لنگه از روی همان کف شروع
// می‌شود (مثلِ آستانه‌ی واقعی) و به همان اندازه کوتاه‌تر است؛ چارچوب و زهوار از کفِ پایین‌تر شروع می‌شوند
// hinge: 'start' (پیش‌فرض) | 'end' — لولا سمتِ دیگرِ در (آینه‌ای)؛ جهتِ بازشدن (facing) همان می‌ماند
function Door({ width, height, thickness, centerAlong, style, isOpen, facing, hinge, isSelected, onSelect, baseY = 0, casingDrop, protrude }) {
  if (width <= 0 || height <= 0) return null;
  const s = style || DEFAULT_DOOR_STYLE;
  const swingSign = facing === 'in' ? -1 : 1;
  const targetAngle = isOpen ? OPEN_ANGLE : 0;
  const isDouble = s.type === 'double';
  const isDoubleSliding = s.type === 'doubleSliding';
  const isSliding = s.type === 'sliding' || s.type === 'barn';
  const isFolding = s.type === 'folding';
  const isHinged = !isSliding && !isDoubleSliding;

  // دهانه‌ی داخلِ چارچوب و ابعادِ دقیقِ لنگه
  const innerW = Math.max(10, width - JAMB_CM * 2);
  const leafH = Math.max(10, height - JAMB_CM - LEAF_GAP_CM - FLOOR_GAP_CM - baseY);
  // لنگه‌ی لولایی به سمتِ -Z محلی باز می‌شود وقتی swingSign=+1 (چرخشِ مثبت حولِ Y) — openZ همان سمت است
  const openZ = -swingSign;
  const pivotZ = openZ * (thickness / 2 + WALL_FACE_OFFSET_CM);
  const contentZ = -openZ * (LEAF_THICKNESS_CM / 2);
  // رنگِ چارچوب: برای درهای شیشه‌ای/کشویی (قابِ فلزی = accent) همان رنگِ قابِ لنگه، وگرنه رنگِ اصلی
  const frameColor = s.type === 'glassModern' || s.type === 'sliding' ? s.accent : s.color;

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect();
  };

  let leaves;
  if (isFolding) {
    leaves = (
      <DoorFoldLeaf
        width={innerW - LEAF_GAP_CM * 2}
        height={leafH}
        style={s}
        swingSign={swingSign}
        targetAngle={targetAngle}
        pivotZ={pivotZ}
        contentZ={contentZ}
      />
    );
  } else if (isDoubleSliding) {
    const leafW = (innerW - LEAF_GAP_CM * 3) / 2;
    const restAbs = LEAF_GAP_CM / 2 + leafW / 2;
    leaves = (
      <>
        {[-1, 1].map((sx) => (
          <DoorSlideLeaf
            key={sx}
            leafWidth={leafW}
            height={leafH}
            thickness={thickness}
            style={s}
            isOpen={isOpen}
            restX={sx * restAbs}
            mountSign={swingSign}
            handleSide={-sx}
            slideDistance={innerW / 2 - LEAF_GAP_CM / 2}
          />
        ))}
      </>
    );
  } else if (isSliding) {
    const leafW = innerW - LEAF_GAP_CM * 2;
    leaves = (
      <DoorSlideLeaf
        leafWidth={leafW}
        height={leafH}
        thickness={thickness}
        style={s}
        isOpen={isOpen}
        handleSide={swingSign}
        mountSign={swingSign}
        slideDistance={innerW - LEAF_GAP_CM}
      />
    );
  } else if (isDouble) {
    const leafW = (innerW - LEAF_GAP_CM * 3) / 2;
    const hingeAbs = innerW / 2 - LEAF_GAP_CM;
    leaves = (
      <>
        <DoorLeaf
          hingeX={-hingeAbs}
          hingeDir={1}
          swingSign={swingSign}
          leafWidth={leafW}
          height={leafH}
          style={s}
          targetAngle={targetAngle}
          pivotZ={pivotZ}
          contentZ={contentZ}
        />
        <DoorLeaf
          hingeX={hingeAbs}
          hingeDir={-1}
          swingSign={-swingSign}
          leafWidth={leafW}
          height={leafH}
          style={s}
          targetAngle={targetAngle}
          pivotZ={pivotZ}
          contentZ={contentZ}
        />
      </>
    );
  } else {
    leaves = (
      <DoorLeaf
        hingeX={-(innerW / 2 - LEAF_GAP_CM)}
        hingeDir={1}
        swingSign={swingSign}
        leafWidth={innerW - LEAF_GAP_CM * 2}
        height={leafH}
        style={s}
        targetAngle={targetAngle}
        pivotZ={pivotZ}
        contentZ={contentZ}
      />
    );
  }

  return (
    <group position={[centerAlong, 0, 0]} onClick={handleClick}>
      {isSelected && (
        <mesh position={[0, height + CASING_WIDTH_CM + 4, 0]}>
          <boxGeometry args={[width * 0.98, 3, thickness + 4]} />
          <meshBasicMaterial color="#0a66c2" />
        </mesh>
      )}
      <DoorFrame
        width={width}
        height={height}
        thickness={thickness}
        style={s}
        frameColor={frameColor}
        stopZSign={isHinged ? openZ : 0}
        baseY={baseY}
        casingDrop={casingDrop}
        protrude={protrude}
      />
      {/* khLeaf: لنگه‌های متحرک — در نمایشِ «رندرِ واقعی» همین لنگه‌های زنده (باز/بسته‌شونده) دیده می‌شوند */}
      <group position={[0, baseY + FLOOR_GAP_CM, 0]} scale={[hinge === 'end' ? -1 : 1, 1, 1]} userData={{ khLeaf: true }}>
        {leaves}
      </group>
    </group>
  );
}

export default Door;
